import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import { ERROR_CODES } from '~/api/serverErrors.ts';
import { appConfig } from '~/consts.ts';
import {
  AppContexts,
  getRandomInteger,
  isDevelopmentMode,
  sleep
} from '~/utils/commonUtils.ts';
import { createApiError, isApiError } from '~/api/ApiResponse.ts';
import { globalStore } from '~/stores';
import { ScalekitAxiosConfig } from '~/api/Request.ts';
import logger from '~/utils/logger';

export const API_HOST = '';
export const APP_LOGOUT_PATH = '/app/logout';
export const APP_LOGIN_PATH = '/app/auth/initiate';
export const HOSTED_AUTH_INITIATE_LOGIN_PATH = '/sso/v1/login/initiate';
export const OAUTH_CONSENT_SUBMIT_PATH = '/oauth/consent:submit';
export const PLATFORM_LOGOUT_CURRENT_USER = '/oidc/logout';
export const APP_WORKSPACE_SWITCH_PATH = '/app/auth/switch';
export const REAUTH_VERIFY_CALLBACK_PATH = '/sso/v1/verify/callback';

const retryAuthorizedRequests = (failureCount: any, error: any) => {
  if (
    isApiError(error) &&
    (error.httpStatus === HttpStatusCode.Unauthorized ||
      error.httpStatus === HttpStatusCode.NotFound ||
      error.httpStatus === HttpStatusCode.BadRequest ||
      error.httpStatus === HttpStatusCode.Forbidden)
  ) {
    return false;
  }
  return failureCount < appConfig.apiRetries;
};

const retryServerErrors = (failureCount: any, error: any) => {
  if (isApiError(error) && !isApi5xxResponseStatus(error.httpStatus)) {
    return false;
  }
  return failureCount < appConfig.apiRetries;
};

// Specifically written for local environment to have a 200 - 500ms delay for all api calls
const apiSleep = async () => {
  if (!isDevelopmentMode()) {
    return;
  }
  return await sleep(getRandomInteger(1, 50));
};

const withMakeRequest: WithApiCall<
  (...args: any[]) => Promise<AxiosResponse>,
  (data: any) => any
> = (fetcher, modifier) => {
  return async (
    ...args: Parameters<(...args: any[]) => Promise<AxiosResponse>>
  ) => {
    await apiSleep();
    try {
      const res = await fetcher(...args);

      if (String(res.status).startsWith('2')) {
        try {
          if (res.data) {
            const data =
              typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
            return modifier ? modifier(data) : data;
          }
          return res.data;
        } catch (parseError) {
          // got success response but not JSON response
          // @todo need to handle better
          logger.error(
            `Failed to parse response data for api ${res.config.url}`,
            parseError
          );
          return {};
        }
      }
      throw createApiError({
        errorCode: ERROR_CODES.UNKNOWN,
        status: res.status
      });
    } catch (e: any) {
      let errorCode = ERROR_CODES.GENERIC;
      let apiError;

      if (e.isAxiosError) {
        if (e.response?.status === HttpStatusCode.Unauthorized) {
          errorCode = ERROR_CODES.UNAUTHORIZED;
        } else if (e.response?.status === HttpStatusCode.Forbidden) {
          errorCode = ERROR_CODES.FORBIDDEN;
        } else if (String(e.response?.status).startsWith('4')) {
          errorCode = ERROR_CODES.BAD_REQUEST;
        } else if (isApi5xxResponseStatus(e.response?.status)) {
          errorCode = ERROR_CODES.SERVER_RESPONSE_5XX;
        } else {
          errorCode = ERROR_CODES.UNKNOWN;
        }

        apiError = createApiError({
          errorCode,
          status: e.response?.status as HttpStatusCode,
          error: getDetailsFromError(e)
        });
      } else if (isApiError(e)) {
        apiError = e;
      }
      throw apiError;
    }
  };
};

const getRequestHeaders = () => {
  const token =
    globalStore.appContext === AppContexts.AGENT_PORTAL
      ? globalStore.agentPortalAuthToken
      : globalStore.portalAuthToken;
  return {
    Authorization: `Bearer ${token}`
  };
};

const getDetailsFromError = (error: AxiosError) => {
  try {
    const data = error.response?.data;
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
  } catch (e) {}
  return {};
};

const isApi5xxResponseStatus = (httpStatus: number) => {
  return String(httpStatus).startsWith('5');
};

const getRequestConfig = (
  config: ScalekitAxiosConfig
): ScalekitAxiosConfig => ({
  addXRequestedWith: true,
  camelCaseResponse: true,
  ...config
});

const getPlatformLogoutUrl = (postRedirectLogoutUri?: string) => {
  let url = `${PLATFORM_LOGOUT_CURRENT_USER}?confirm_logout=yes`;
  if (postRedirectLogoutUri) {
    url += `&post_logout_redirect_uri=${postRedirectLogoutUri}`;
  }
  return url;
};

export {
  apiSleep,
  retryAuthorizedRequests,
  retryServerErrors,
  withMakeRequest,
  getRequestHeaders,
  isApi5xxResponseStatus,
  getRequestConfig,
  getPlatformLogoutUrl
};
